import { useState, useEffect, useRef } from 'react';
import { Link, Route, useNavigate } from 'react-router-dom';
import api, { BACKEND_URL } from '../api/axios';
import Navbar from './Navbar';
import Footer from './Footer';
import JobCard from './JobCard';
import AggregatedJobCard from './AggregatedJobCard';
import data from '../seo/landingData';

import imgSoftware from '../assets/software.jpg';
import imgDoctor from '../assets/doctor.jpg';
import imgBank from '../assets/bank.jpg';
import imgBpo from '../assets/bpo.jpg';
import imgManufacturing from '../assets/manufacturing.jpg';
import imgLogistics from '../assets/logistic.jpg';
import imgHotel from '../assets/hotel.jpg';
import imgTeacher from '../assets/teacher.jpg';
import imgHr from '../assets/HR.jpg';
import imgSales from '../assets/sales.jpg';
import imgMarketing from '../assets/marketing.jpg';
import imgConstruction from '../assets/constructour.jpg';
import imgBengaluru from '../assets/bengaluru.jpg';
import imgDelhi from '../assets/delhi.jpg';
import imgHyderabad from '../assets/hydrabad.jpg';
import imgKolkata from '../assets/kolkat.jpg';
import imgMumbai from '../assets/mumbi.jpg';

const { PAGES, CITIES, INDUSTRIES, TITLES, EXPERIENCE, WORK, RESOURCE_CATEGORIES } = data;

/* One component renders every programmatic SEO page (cities, industries, titles,
   experience, work mode). Copy comes from src/seo/landingData.js, listings from the API.
   App.jsx:  import { seoLandingRoutes } from './pages/SeoLanding';  ...  {seoLandingRoutes} */

const SITE = process.env.REACT_APP_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : '');
const MIN_JOBS_TO_INDEX = 1;
const PAGE_SIZE = 10;

const unwrap = (res) => (Array.isArray(res.data) ? res.data : (res.data?.data ?? []));
const resolveLogoSrc = (logo) => (!logo ? null : logo.startsWith('http') ? logo : `${BACKEND_URL}/storage/${logo}`);
const byPath = Object.fromEntries(PAGES.map((p) => [p.path, p]));
const industryBySlug = Object.fromEntries(INDUSTRIES.map((p) => [p.slug, p]));
const titleBySlug = Object.fromEntries(TITLES.map((p) => [p.slug, p]));
const cityBySlug = Object.fromEntries(CITIES.map((p) => [p.slug, p]));
const jobText = (j) => `${j.title || ''} ${j.company_name || j.company || ''}`.toLowerCase();
const linkLabel = (i) => (i.type === 'city' ? `Jobs in ${i.name}` : i.name);

// First match wins. Add a line here for any new city or industry photo.
const IMAGE_RULES = [
  [/bengaluru|bangalore/, imgBengaluru],
  [/delhi/, imgDelhi],
  [/hyderabad/, imgHyderabad],
  [/kolkata/, imgKolkata],
  [/mumbai/, imgMumbai],
  [/construction|civil|site engineer/, imgConstruction],
  [/information technology|software|developer|programmer|\bit\b/, imgSoftware],
  [/health|doctor|nurse|medical|hospital|pharma/, imgDoctor],
  [/bank|financ|account/, imgBank],
  [/bpo|customer|support|call cent/, imgBpo],
  [/manufactur|production|factory/, imgManufacturing],
  [/logistic|supply|delivery|driver|warehouse/, imgLogistics],
  [/hospitality|retail|hotel|chef/, imgHotel],
  [/educat|teacher|admin/, imgTeacher],
  [/human resource|\bhr\b|recruit/, imgHr],
  [/sales|business development/, imgSales],
  [/marketing|seo|content/, imgMarketing],
];

const pageImage = (page) => {
  const text = `${page.name || ''} ${page.slug || ''} ${page.industry || ''}`.toLowerCase();
  const hit = IMAGE_RULES.find(([re]) => re.test(text));
  if (hit) return hit[1];
  if (page.type === 'city') return imgMumbai;
  if (page.type === 'experience') return imgSales;
  return imgSoftware;
};

/* ------------------------------- SEO head ------------------------------- */
export function useSeo({ title, description, canonical, noindex, jsonLd }) {
  const ldKey = JSON.stringify(jsonLd || []);
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;
    const undo = [];

    const setMeta = (name, content) => {
      let m = document.querySelector(`meta[name="${name}"]`);
      const created = !m;
      if (!m) { m = document.createElement('meta'); m.name = name; document.head.appendChild(m); }
      const prev = m.getAttribute('content');
      m.setAttribute('content', content);
      undo.push(() => { if (created) m.remove(); else if (prev !== null) m.setAttribute('content', prev); });
    };
    setMeta('description', description);
    if (noindex) setMeta('robots', 'noindex,follow');

    let link = document.querySelector('link[rel="canonical"]');
    const linkCreated = !link;
    const prevHref = link ? link.getAttribute('href') : null;
    if (!link) { link = document.createElement('link'); link.rel = 'canonical'; document.head.appendChild(link); }
    link.setAttribute('href', canonical);
    undo.push(() => { if (linkCreated) link.remove(); else if (prevHref) link.setAttribute('href', prevHref); });

    JSON.parse(ldKey).forEach((obj) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-landing-seo', '1');
      s.text = JSON.stringify(obj);
      document.head.appendChild(s);
      undo.push(() => s.remove());
    });

    return () => { document.title = prevTitle; undo.forEach((fn) => fn()); };
  }, [title, description, canonical, noindex, ldKey]);
}

/* ------------------------------- listings ------------------------------- */
function departmentsFor(L, categories) {
  if (L.department) return [L.department];
  if (L.departmentHint) {
    const re = new RegExp(L.departmentHint, 'i');
    return categories.filter((c) => re.test(c.name)).slice(0, 3).map((c) => c.slug);
  }
  return [];
}

function baseFilters(page, categories) {
  const L = page.listing || {};
  const f = {};
  const depts = departmentsFor(L, categories);
  if (depts.length) f.department = depts[0];
  else if (L.keyword) f.keyword = L.keyword;
  if (L.locations && L.locations.length) f.location = L.locations[0];
  if (L.job_type) f.job_type = L.job_type;
  if (L.work_mode) f.work_mode = L.work_mode;
  if (L.experience) f.experience = L.experience;
  return f;
}

const toQuery = (f) => {
  const q = new URLSearchParams();
  Object.entries(f).forEach(([k, v]) => { if (v) q.set(k, v); });
  const s = q.toString();
  return s ? `/jobs?${s}` : '/jobs';
};

async function loadListings(page, categories) {
  const L = page.listing || {};
  const depts = departmentsFor(L, categories);
  const locations = L.locations && L.locations.length ? L.locations : [null];
  const deptList = page.type === 'title' || !depts.length ? [null] : depts;

  const combos = [];
  locations.forEach((loc) => deptList.forEach((dept) => combos.push({ loc, dept })));
  const limited = combos.slice(0, 6);

  const regularParams = limited.map(({ loc, dept }) => {
    const p = {};
    if (L.job_type) p.job_type = L.job_type;
    if (L.work_mode) p.work_mode = L.work_mode;
    if (L.experience) p.experience = L.experience;
    if (loc) p.location = loc;
    if (dept) p.department = dept;
    else if (L.keyword) p.keyword = L.keyword;
    return p;
  });

  // Mirror JobSearch: aggregated jobs ignore job type and work mode, so skip them there.
  const includeAggregated = !L.job_type && !L.work_mode && L.minExp == null && L.maxExp == null;
  const aggParams = includeAggregated
    ? limited.map(({ loc, dept }) => {
        const p = {};
        if (L.experience) p.experience = L.experience;
        if (loc) p.location = loc;
        if (dept) p.category = dept;
        else if (L.keyword) p.keyword = L.keyword;
        return p;
      })
    : [];

  const [regular, aggregated] = await Promise.all([
    Promise.allSettled(regularParams.map((params) => api.get('/jobs/search', { params }))),
    Promise.allSettled(aggParams.map((params) => api.get('/aggregated-jobs', { params }))),
  ]);

  if (regular.length && regular.every((r) => r.status === 'rejected')) throw new Error('listing failed');

  const seen = new Set();
  const out = [];
  regular.forEach((r) => {
    if (r.status !== 'fulfilled') return;
    unwrap(r.value).forEach((j) => {
      const key = `reg-${j.id}`;
      if (seen.has(key)) return;
      const exp = j.experience_required;
      if (L.minExp != null && !(typeof exp === 'number' && exp >= L.minExp)) return;
      if (L.maxExp != null && !(typeof exp === 'number' && exp <= L.maxExp)) return;
      seen.add(key);
      out.push({ ...j, _type: 'regular', _key: key });
    });
  });
  aggregated.forEach((r) => {
    if (r.status !== 'fulfilled') return;
    unwrap(r.value).forEach((j) => {
      const key = `agg-${j.id}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push({ ...j, _type: 'aggregated', _key: key });
    });
  });
  return out;
}

function Reveal({ children, delay = 0, className = '', variant = 'up' }) {
  const ref = useRef(null);
  const [on, setOn] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!('IntersectionObserver' in window)) { setOn(true); return; }
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setOn(true); io.disconnect(); } },
      { threshold: 0.1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`reveal ${variant !== 'up' ? `reveal-${variant}` : ''} ${on ? 'reveal-in' : ''} ${className}`}
    >
      {children}
    </div>
  );
}

function ScrollProgress() {
  const [p, setP] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setP(h > 0 ? (window.scrollY / h) * 100 : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return <div className="fixed top-0 left-0 h-[3px] z-[100] brand-gradient" style={{ width: `${p}%` }} aria-hidden="true" />;
}

/* ------------------------------ small pieces ------------------------------ */
const chipCls = 'inline-block px-3.5 py-1.5 rounded-full text-sm font-semibold text-[#7C3AED] bg-[#F5F0FA] hover:bg-[#EBDDFB] hover:-translate-y-0.5 hover:shadow-md transition-all duration-300 whitespace-nowrap';

function Chip({ to, children }) {
  return <Link to={to} className={chipCls}>{children}</Link>;
}

function Arrow() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

function Block({ title, children }) {
  return (
    <Reveal className="h-full">
      <div className="h-full rounded-2xl border border-purple-100 bg-white p-5 lg:p-6 transition-all duration-300 hover:-translate-y-1 hover:border-[#7C3AED]/50 hover:shadow-[0_20px_40px_-20px_rgba(124,58,237,0.5)]">
        <h3 className="text-base font-bold mb-3">{title}</h3>
        {children}
      </div>
    </Reveal>
  );
}

function Chips({ children }) {
  return <div className="flex flex-wrap gap-2">{children}</div>;
}

function Text({ children }) {
  return <p className="text-sm text-gray-600 leading-relaxed">{children}</p>;
}

function BulletList({ items }) {
  return (
    <ul className="space-y-2.5">
      {items.map((t) => (
        <li key={t} className="flex items-start gap-2.5 text-sm text-gray-600 leading-relaxed">
          <span className="w-2 h-2 mt-2 rounded-full bg-[#DB2777] shrink-0" />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}

function Faq({ faqs }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="space-y-3">
      {faqs.map((f, i) => {
        const isOpen = open === i;
        return (
          <Reveal key={f.q} delay={(i % 5) * 60}>
            <div className={`bg-white border rounded-2xl overflow-hidden transition-all duration-300 ${isOpen ? 'border-[#DB2777]/40 shadow-[0_20px_40px_-22px_rgba(124,58,237,0.45)]' : 'border-purple-100 hover:border-[#7C3AED]/40'}`}>
              <button
                type="button" onClick={() => setOpen(isOpen ? null : i)} aria-expanded={isOpen} aria-controls={`landing-faq-${i}`}
                className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]/50"
              >
                <span>{f.q}</span>
                <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xl leading-none shrink-0 transition-all duration-300 ${isOpen ? 'rotate-45 brand-gradient text-white' : 'bg-[#F5F0FA] text-[#DB2777]'}`} aria-hidden="true">+</span>
              </button>
              <div className={`grid transition-all duration-300 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                <div className="overflow-hidden">
                  <p id={`landing-faq-${i}`} className="px-5 pb-5 text-sm text-gray-500 leading-relaxed">{f.a}</p>
                </div>
              </div>
            </div>
          </Reveal>
        );
      })}
    </div>
  );
}

/* Guide blocks, one list per page type */
function guideBlocks(page, categories, filters) {
  const loc = filters.location;
  const kw = filters.keyword;
  const dept = filters.department;
  const resources = page.resources && page.resources.length ? (
    <Block key="res" title="Career resources">
      <Chips>{page.resources.map((s) => <Chip key={s} to={`/career-resources?category=${s}`}>{RESOURCE_CATEGORIES[s]}</Chip>)}</Chips>
    </Block>
  ) : null;

  if (page.type === 'city') {
    return [
      <Block key="ind" title={`Major hiring industries in ${page.name}`}>
        <Chips>{page.industries.map((s) => industryBySlug[s] && <Chip key={s} to={industryBySlug[s].path}>{industryBySlug[s].name}</Chip>)}</Chips>
      </Block>,
      <Block key="cat" title="Popular job categories">
        <Chips>
          {categories.slice(0, 8).map((c) => (
            <Chip key={c.slug} to={`/jobs?location=${encodeURIComponent(loc)}&department=${encodeURIComponent(c.slug)}`}>{c.name}</Chip>
          ))}
          {categories.length === 0 && <Chip to={toQuery({ location: loc })}>All categories</Chip>}
        </Chips>
      </Block>,
      <Block key="fr" title={`Fresher opportunities in ${page.name}`}>
        <Text>{page.fresher}</Text>
        <Link to={toQuery({ location: loc, experience: 'fresher' })} className="inline-flex items-center gap-1.5 mt-3 text-sm font-bold text-[#DB2777] hover:underline">Fresher jobs in {page.name} <Arrow /></Link>
      </Block>,
      <Block key="ex" title="Experienced professionals">
        <Text>{page.experienced}</Text>
        <Link to="/jobs-for-experienced" className="inline-flex items-center gap-1.5 mt-3 text-sm font-bold text-[#DB2777] hover:underline">Jobs for experienced professionals <Arrow /></Link>
      </Block>,
      <Block key="rem" title="Remote and hybrid jobs">
        <Text>{page.remote}</Text>
        <div className="mt-3"><Chips>
          <Chip to={toQuery({ location: loc, work_mode: 'hybrid' })}>Hybrid in {page.name}</Chip>
          <Chip to="/work-from-home-jobs">Work from home jobs</Chip>
        </Chips></div>
      </Block>,
      <Block key="tip" title={`Good to know about ${page.name}`}><Text>{page.tip}</Text></Block>,
    ];
  }
  if (page.type === 'industry') {
    const locLink = (c) => toQuery({ location: c.listing.locations[0], ...(dept ? { department: dept } : { keyword: kw }) });
    return [
      <Block key="t" title="Popular job titles">
        <Chips>{page.titles.map((t) => <Chip key={t.name} to={t.slug ? `/jobs/${t.slug}` : toQuery({ keyword: t.name })}>{t.name}</Chip>)}</Chips>
      </Block>,
      <Block key="s" title="Skills employers ask for"><BulletList items={page.skills} /></Block>,
      <Block key="e" title="Filter by experience">
        <Chips>
          <Chip to={toQuery({ ...(dept ? { department: dept } : { keyword: kw }), experience: 'fresher' })}>Freshers</Chip>
          {EXPERIENCE.filter((e) => e.slug !== 'freshers').map((e) => <Chip key={e.path} to={e.path}>{e.name}</Chip>)}
        </Chips>
      </Block>,
      <Block key="l" title="Related locations">
        <Chips>{CITIES.slice(0, 8).map((c) => <Chip key={c.slug} to={locLink(c)}>{page.name} jobs in {c.name}</Chip>)}</Chips>
      </Block>,
      resources,
    ];
  }
  if (page.type === 'title') {
    return [
      <Block key="d" title="What the role involves"><BulletList items={page.duties} /></Block>,
      <Block key="s" title="Skills employers ask for"><BulletList items={page.skills} /></Block>,
      resources,
    ];
  }
  return [<Block key="tips" title="Things to keep in mind"><BulletList items={page.tips} /></Block>, resources];
}

function relatedLinks(page) {
  let list = [];
  if (page.type === 'city') list = (page.related || []).map((s) => cityBySlug[s]);
  else if (page.type === 'title') list = [page.industry && industryBySlug[page.industry], ...(page.related || []).map((s) => titleBySlug[s])];
  else if (page.type === 'industry') list = INDUSTRIES.filter((i) => i.path !== page.path).slice(0, 8);
  else list = (page.type === 'experience' ? EXPERIENCE : WORK).filter((s) => s.path !== page.path);
  return list.filter(Boolean);
}

/* --------------------------------- page --------------------------------- */
function SeoLanding({ page }) {
    const navigate = useNavigate();
  const goBack = () => (window.history.length > 1 ? navigate(-1) : navigate('/jobs'));
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [shown, setShown] = useState(PAGE_SIZE);
  const [query, setQuery] = useState('');
  const [introOpen, setIntroOpen] = useState(false);
  const [extras, setExtras] = useState({ employers: [], faqs: [] });

  const [user] = useState(() => {
    const s = localStorage.getItem('user');
    return s ? JSON.parse(s) : null;
  });
  const isLoggedIn = !!localStorage.getItem('token');

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setShown(PAGE_SIZE);
    setQuery('');
    setIntroOpen(false);
    (async () => {
      let cats = [];
      try { cats = unwrap(await api.get('/job-categories')); } catch (e) { cats = []; }
      if (cancelled) return;
      setCategories(cats);
      try {
        const list = await loadListings(page, cats);
        if (!cancelled) { setJobs(list); setStatus('ready'); }
      } catch (e) {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, [page]);

  // Reuse the backend slug resolver (popular employers + FAQs) when it knows this page
  useEffect(() => {
    let cancelled = false;
    setExtras({ employers: [], faqs: [] });
    if (!['city', 'industry', 'title'].includes(page.type)) return undefined;
    const candidates = [...new Set([page.slug, ...((page.listing.locations || []).map((l) => l.toLowerCase()))])];
    (async () => {
      for (const slug of candidates) {
        try {
          const res = await api.get(`/slugs/${slug}`);
          if (!cancelled) setExtras({ employers: res.data.popular_employers || [], faqs: res.data.faqs || [] });
          return;
        } catch (e) { /* try the next candidate */ }
      }
    })();
    return () => { cancelled = true; };
  }, [page]);

  const filters = baseFilters(page, categories);
  const allJobsLink = toQuery(filters);
  const canonical = `${SITE}${page.path}`;
  const thin = status === 'ready' && jobs.length < MIN_JOBS_TO_INDEX;
  const seenQ = new Set(page.faqs.map((f) => f.q));
  const allFaqs = [
    ...page.faqs,
    ...extras.faqs.filter((f) => f.question && f.answer && !seenQ.has(f.question)).map((f) => ({ q: f.question, a: f.answer })),
  ];

  const jsonLd = [
    {
      '@context': 'https://schema.org', '@type': 'BreadcrumbList',
      itemListElement: [
        { name: 'Home', url: `${SITE}/` },
        { name: 'Jobs', url: `${SITE}/jobs` },
        { name: page.name, url: canonical },
      ].map((b, i) => ({ '@type': 'ListItem', position: i + 1, name: b.name, item: b.url })),
    },
    {
      '@context': 'https://schema.org', '@type': 'FAQPage',
      mainEntity: allFaqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
    },
  ];
  if (status === 'ready' && jobs.length) {
    jsonLd.push({
      '@context': 'https://schema.org', '@type': 'ItemList',
      itemListElement: jobs.filter((j) => j._type === 'regular').slice(0, 10).map((j, i) => ({
        '@type': 'ListItem', position: i + 1, url: `${SITE}/jobs/${j.id}`, name: j.title,
      })),
    });
  }
  useSeo({ title: page.title, description: page.description, canonical, noindex: thin, jsonLd });

  const quick = [
    !filters.experience && { label: 'Freshers', to: toQuery({ ...filters, experience: 'fresher' }) },
    !filters.job_type && { label: 'Part-time', to: toQuery({ ...filters, job_type: 'part_time' }) },
    !filters.job_type && { label: 'Internships', to: toQuery({ ...filters, job_type: 'internship' }) },
    !filters.work_mode && { label: 'Work from home', to: toQuery({ ...filters, work_mode: 'work_from_home' }) },
    !filters.work_mode && { label: 'Hybrid', to: toQuery({ ...filters, work_mode: 'hybrid' }) },
  ].filter(Boolean);

  const q = query.trim().toLowerCase();
  const visible = q ? jobs.filter((j) => jobText(j).includes(q)) : jobs;

  const exploreGroups = [
    { title: 'Jobs by city', items: CITIES },
    { title: 'Jobs by industry', items: INDUSTRIES },
    { title: 'Popular job titles', items: TITLES },
    { title: 'By experience and work mode', items: [...EXPERIENCE, ...WORK] },
  ];
  const related = relatedLinks(page);
  const bg = pageImage(page);
  const guideTitle = page.type === 'city' ? `Working in ${page.name}` : page.type === 'title' ? `About the ${page.name} role` : page.type === 'industry' ? `Careers in ${page.name.toLowerCase()}` : 'Before you apply';

  return (
    <div
      className="min-h-screen bg-[#FBF9FE] overflow-x-clip text-[#1E0F3D]"
      style={{
        backgroundImage: `linear-gradient(rgba(251,249,254,.8), rgba(251,249,254,.8)), url(${bg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}
    >
      <style>{`
  .brand-gradient{background-image:linear-gradient(90deg,#7C3AED,#DB2777)}
  .no-bar{scrollbar-width:none}.no-bar::-webkit-scrollbar{display:none}

  .animated-gradient{background:linear-gradient(90deg,#7C3AED,#DB2777,#7C3AED);background-size:200% 100%;animation:gradient-shift 8s ease infinite}

  .reveal{opacity:0;transform:translateY(28px);transition:opacity .7s cubic-bezier(.2,.8,.2,1),transform .7s cubic-bezier(.2,.8,.2,1)}
  .reveal-right{transform:translateX(48px)}
  .reveal-zoom{transform:scale(.94)}
  .reveal-in{opacity:1;transform:none}

  @keyframes rise{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:none}}
  @keyframes float{0%,100%{transform:translateY(0) scale(1)}50%{transform:translateY(-24px) scale(1.06)}}
  @keyframes shimmer{to{background-position:200% center}}
  @keyframes gradient-shift{0%,100%{background-position:0% 50%}50%{background-position:100% 50%}}
  @keyframes cta-glow{0%{box-shadow:0 0 0 0 rgba(219,39,119,.45)}100%{box-shadow:0 0 0 18px rgba(219,39,119,0)}}

  .rise{animation:rise .8s cubic-bezier(.2,.8,.2,1) both}
  .float-slow{animation:float 9s ease-in-out infinite}
  .float-slower{animation:float 13s ease-in-out infinite reverse}
  .shimmer-text{background-size:200% auto;animation:shimmer 5s linear infinite}
  .cta-glow{animation:cta-glow 2.4s ease-out infinite}

  @media (prefers-reduced-motion: reduce){
    .rise,.float-slow,.float-slower,.shimmer-text,.cta-glow,.animated-gradient{animation:none}
    .reveal{opacity:1;transform:none;transition:none}
  }
`}</style>

      <ScrollProgress />
<Navbar user={user} />

     {/* HERO */}
<header className="relative overflow-hidden border-b border-purple-100">
  <img src={bg} alt="" className="absolute inset-0 h-full w-full object-cover object-top" aria-hidden="true" />
  <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/65 to-[#FBF5FF]/90" aria-hidden="true" />
  <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
    <div className="absolute -top-32 -right-20 w-[28rem] h-[28rem] rounded-full bg-[#7C3AED]/15 blur-3xl float-slow" />
    <div className="absolute -bottom-32 -left-20 w-[24rem] h-[24rem] rounded-full bg-[#DB2777]/12 blur-3xl float-slower" />
    <div className="absolute inset-0 opacity-60" style={{ backgroundImage: 'radial-gradient(rgba(124,58,237,0.10) 1px, transparent 1px)', backgroundSize: '26px 26px' }} />
  </div>

  <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 py-7 lg:py-10 text-center">
    <div className="rise flex justify-start mb-3">
      <button
        type="button"
        onClick={goBack}
        className="group inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-purple-100 text-sm font-semibold text-[#7C3AED] shadow-sm transition-all duration-300 hover:-translate-x-0.5 hover:bg-[#F5F0FA] hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED]/50"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-1 motion-reduce:transition-none" aria-hidden="true">
          <path d="M19 12H5M11 6l-6 6 6 6" />
        </svg>
        Back
      </button>
    </div>

    <nav aria-label="Breadcrumb" className="rise text-xs font-medium text-gray-700 mb-3">
      <ol className="flex flex-wrap items-center justify-center gap-1.5">
        <li><Link to="/" className="hover:text-[#7C3AED]">Home</Link></li>
        <li aria-hidden="true">/</li>
        <li><Link to="/jobs" className="hover:text-[#7C3AED]">Jobs</Link></li>
        <li aria-hidden="true">/</li>
        <li className="font-semibold text-[#1E0F3D]" aria-current="page">{page.name}</li>
      </ol>
    </nav>

    <h1
      key={page.path}
      className="rise shimmer-text mx-auto mb-3 w-fit max-w-full text-3xl sm:text-5xl font-extrabold tracking-tight leading-tight bg-gradient-to-r from-[#5B21B6] via-[#BE185D] to-[#5B21B6] bg-clip-text text-transparent"
      style={{ animationDelay: '.08s' }}
    >
      {page.h1}
    </h1>

    <p className={`rise max-w-3xl mx-auto text-[15px] leading-7 font-medium text-[#1E0F3D] ${introOpen ? '' : 'line-clamp-3 sm:line-clamp-none'}`} style={{ animationDelay: '.16s' }}>
      {page.intro}
    </p>
    {!introOpen && (
      <button type="button" onClick={() => setIntroOpen(true)} className="sm:hidden mt-1 text-sm font-semibold text-[#7C3AED]">Read more</button>
    )}

    <div className="rise flex flex-wrap items-center justify-center gap-3 mt-5" style={{ animationDelay: '.24s' }}>
      <button type="button" onClick={() => navigate(allJobsLink)} className="brand-gradient cta-glow text-white px-6 py-3 rounded-xl font-semibold text-sm transition hover:-translate-y-0.5 hover:opacity-95">
        Search all matching jobs
      </button>
      {status === 'ready' && (
        <span className="inline-flex items-center gap-2 bg-white border border-purple-100 rounded-full px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm">
          <span className="relative flex w-2 h-2">
            <span className="absolute inline-flex w-full h-full rounded-full bg-[#DB2777] opacity-75 animate-ping motion-reduce:animate-none" />
            <span className="relative inline-flex w-2 h-2 rounded-full bg-[#DB2777]" />
          </span>
          {jobs.length} active {jobs.length === 1 ? 'vacancy' : 'vacancies'}
        </span>
      )}
    </div>
  </div>
</header>

      {/* LISTINGS + SIDEBAR */}
      <main className="max-w-7xl mx-auto px-5 lg:px-8 py-8 lg:py-10 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-8">
        <section id="listings" className="scroll-mt-24 min-w-0">
          <div className="flex gap-2 overflow-x-auto no-bar pb-3 -mx-5 px-5 lg:mx-0 lg:px-0" aria-label="Quick filters">
            {quick.map((c) => <Chip key={c.label} to={c.to}>{c.label}</Chip>)}
          </div>

          {status === 'ready' && jobs.length > 0 && (
            <div className="mt-2 mb-5">
              <label htmlFor="landing-filter" className="sr-only">Filter these jobs by title or company</label>
              <input
                id="landing-filter" type="search" value={query}
                onChange={(e) => { setQuery(e.target.value); setShown(PAGE_SIZE); }}
                placeholder="Filter these jobs by title or company"
                className="w-full rounded-xl border border-purple-100 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/40"
              />
              <p className="mt-2 text-xs text-gray-500">Showing {Math.min(shown, visible.length)} of {visible.length} {visible.length === 1 ? 'job' : 'jobs'}</p>
            </div>
          )}

          {status === 'loading' && (
            <div className="space-y-4 mt-4" aria-busy="true" aria-label="Loading jobs">
              {[0, 1, 2].map((i) => <div key={i} className="h-28 rounded-2xl bg-[#F0E8FA] animate-pulse motion-reduce:animate-none" />)}
            </div>
          )}

          {status === 'error' && (
            <div className="rounded-2xl bg-white border border-purple-100 px-6 py-8 text-center mt-4">
              <p className="font-bold mb-1">We could not load jobs right now.</p>
              <p className="text-sm text-gray-500 mb-4">Reload the page, or search all jobs directly.</p>
              <Link to={allJobsLink} className="brand-gradient text-white px-6 py-2.5 rounded-xl text-sm font-semibold inline-block">Search jobs</Link>
            </div>
          )}

          {status === 'ready' && jobs.length === 0 && (
            <div className="rounded-2xl bg-white border border-purple-100 px-6 py-10 text-center mt-4">
              <p className="font-bold text-lg mb-1">No open vacancies match this page right now.</p>
              <p className="text-sm text-gray-500 mb-5">New jobs are added regularly. Try the wider search or a related page.</p>
              <Link to="/jobs" className="brand-gradient text-white px-6 py-2.5 rounded-xl text-sm font-semibold inline-block">Explore all jobs</Link>
            </div>
          )}

          {status === 'ready' && jobs.length > 0 && visible.length === 0 && (
            <p className="rounded-2xl bg-white border border-purple-100 px-6 py-8 text-center text-sm text-gray-600">
              No jobs match “{query}”. <button type="button" onClick={() => setQuery('')} className="font-semibold text-[#7C3AED] hover:underline">Clear filter</button>
            </p>
          )}

          {visible.length > 0 && (
            <>
              <div className="space-y-2.5">
               {visible.slice(0, shown).map((job, idx) => (
  <Reveal key={job._key} delay={(idx % 4) * 70}>
    {job._type === 'aggregated' ? (
      <AggregatedJobCard job={job} BACKEND_URL={BACKEND_URL} />
    ) : (
      <JobCard
        job={job} applied={false} applying={false}
        logoSrc={resolveLogoSrc(job.company_logo || (job.employer && job.employer.company_logo))}
        onApply={() => navigate(`/jobs/${job.id}`)}
      />
    )}
  </Reveal>
))}
              </div>
              <div className="flex flex-wrap items-center gap-4 mt-6">
                {shown < visible.length && (
                  <button type="button" onClick={() => setShown((n) => n + PAGE_SIZE)} className="px-6 py-3 rounded-xl text-sm font-semibold text-[#7C3AED] bg-white border border-[#7C3AED]/40 hover:bg-[#F5F0FA] transition">
                    Show more jobs
                  </button>
                )}
                <Link to={allJobsLink} className="inline-flex items-center gap-1.5 text-sm font-bold text-[#DB2777] hover:underline">Refine with all filters <Arrow /></Link>
              </div>
            </>
          )}
        </section>

        <aside className="space-y-5 lg:sticky lg:top-24 lg:self-start">
          <Reveal variant="right">
<div className="animated-gradient rounded-2xl p-6 text-white shadow-[0_25px_50px_-25px_rgba(124,58,237,0.6)]">
            <p className="text-lg font-bold mb-1">Get matched to jobs like these</p>
            <p className="text-sm text-white/85 mb-4">{isLoggedIn ? 'Keep your profile current so employers can find you.' : 'Create a free profile and apply in a few taps.'}</p>
            <Link to={isLoggedIn ? '/profile' : '/register'} className="block text-center bg-white text-[#7C3AED] rounded-xl py-2.5 text-sm font-bold hover:opacity-90 transition">
              {isLoggedIn ? 'Update my profile' : 'Create free profile'}
            </Link>
          </div>
          </Reveal>

          {related.length > 0 && (
            <div className="rounded-2xl border border-purple-100 bg-white p-5 transition-all duration-300 hover:shadow-[0_20px_40px_-22px_rgba(124,58,237,0.45)]">
              <p className="font-bold text-sm mb-3">Related searches</p>
              <Chips>{related.map((i) => <Chip key={i.path} to={i.path}>{linkLabel(i)}</Chip>)}</Chips>
            </div>
          )}

          {extras.employers.length > 0 && (
            <div className="rounded-2xl border border-purple-100 bg-white p-5">
              <p className="font-bold text-sm mb-3">Popular employers hiring here</p>
              <Chips>
                {extras.employers.map((emp) => (
                  emp.company_slug
                    ? <Chip key={emp.company_slug} to={`/company/${emp.company_slug}`}>{emp.company_name} ({emp.job_count})</Chip>
                    : <span key={emp.company_name} className="inline-block px-3.5 py-1.5 rounded-full text-sm font-semibold text-gray-500 bg-gray-100">{emp.company_name} ({emp.job_count})</span>
                ))}
              </Chips>
            </div>
          )}
        </aside>
      </main>

      {/* GUIDE */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-10">
        <Reveal><h2 className="text-2xl font-bold tracking-tight mb-5">{guideTitle}</h2></Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{guideBlocks(page, categories, filters)}</div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-5 lg:px-8 pb-12">
        <Reveal><h2 className="text-2xl font-bold tracking-tight mb-5">Frequently asked questions</h2></Reveal>
        <Faq faqs={allFaqs} />
      </section>

      {/* EXPLORE MORE: collapsed groups, links stay in the page for crawlers */}
      <section className="bg-white border-t border-purple-100 py-10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <h2 className="text-lg font-bold mb-4">Explore more jobs</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {exploreGroups.map((g, gi) => (
  <Reveal key={g.title} delay={gi * 100}>
    <p className="font-bold text-sm mb-3">{g.title}</p>
    <ul className="space-y-1.5">
      {g.items.filter((i) => i.path !== page.path).map((i) => (
        <li key={i.path}>
          <Link to={i.path} className="inline-block text-sm text-gray-600 hover:text-[#7C3AED] hover:translate-x-1.5 transition-all duration-300">{linkLabel(i)}</Link>
        </li>
      ))}
    </ul>
  </Reveal>
))}
          </div>
          <p className="mt-5 text-sm text-gray-500">
            Hiring? <Link to="/employers" className="font-semibold text-[#7C3AED] hover:underline">Post a job</Link>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default SeoLanding;

/* One <Route> per page, generated from landingData.js.
   Static paths outrank /jobs/:id in React Router, so no change is needed to JobOrLocation. */
export const seoLandingRoutes = PAGES.map((p) => (
  <Route key={p.path} path={p.path} element={<SeoLanding key={p.path} page={byPath[p.path]} />} />
));